import useAuth from '@/hooks/useAuth';
import { useEditCalendarMutation } from '@/queries/calendar.queries';
import { useListOrganizations } from '@/queries/organizations.queries';
import { ICalendar, ICalendarOwner } from '@/types/ICalendar';
import { IOrganization } from '@/types/IOrganization';
import { useToast } from '@chakra-ui/react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';

interface IArgs {
	calendar: ICalendar;
	onClose: () => void;
}

export function useEditCalendarController(args: IArgs) {
	const isMutating = useIsMutating() > 0;
	const isFetching = useIsFetching() > 0;
	const auth = useAuth();
	const orgsQuery = useListOrganizations();
	const editCalendarMutation = useEditCalendarMutation();
	const toast = useToast();

	const form = useForm({ defaultValues: args.calendar });

	const handleSubmit = form.handleSubmit((values) => {
		_saveChanges(values).then(args.onClose);
	});

	function _saveChanges(values: ICalendar) {
		return editCalendarMutation.mutateAsync({ ...values }).then(() => {
			toast({
				status: 'success',
				title: t('Calendar updated'),
			});
		});
	}

	const options =
		orgsQuery.data?.organizations
			// Backend only allows to set calendar if the user is admin of the org
			.filter((org) => {
				const orgMembership = auth.getCurrentUser().organizationMemberships.find((om) => {
					return om.organizationId === org.id;
				});
				if (!org) {
					return false;
				}
				return orgMembership?.roles.some((value) => value === 'ADMIN');
			})
			.map((org) => ({
				id: org.id,
				name: org.name,
				type: 'CalendarOwnerOrg' as ICalendarOwner['type'],
			})) ?? [];

	options.push({
		id: auth.getCurrentUser().userId,
		name: auth.getCurrentUser().email,
		type: 'CalendarOwnerUser',
	});

	return {
		isLoading: isMutating || isFetching,
		selectedOption: options.find((option) => option.id === form.watch('owner').id),
		options,
		orgs: orgsQuery.data?.organizations ?? ([] as Readonly<Array<IOrganization>>),
		form,
		eventModeOption: form.watch('tags')?.TT_PLANNER_MODE === 'DRAFT' ? 'DRAFT' : 'SAVE',
		pendingEventOption: form.watch('tags')?.TT_PENDING_EVENT_FEEDBACK === 'DOT' ? 'DOT' : '',
		borderColorBehavior:
			form.watch('tags')?.TT_BORDER_COLOR_BEHAVIOR === 'EVENT_STATUS' ? 'EVENT_STATUS' : 'ATTENDEE_STATUS',
		statusWhenDatesChanged:
			form.watch('tags')?.TT_STATUS_WHEN_DATES_CHANGED === 'SET_TENTATIVE' ? 'SET_TENTATIVE' : 'KEEP',

		handleSubmit,

		handleEventModeChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.target.value;
			form.setValue('tags', {
				...form.watch('tags'),
				TT_PLANNER_MODE: value as 'DRAFT' | 'SAVE',
			});
		},

		handlePendingEventOptionChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.target.value;
			form.setValue('tags', {
				...form.watch('tags'),
				TT_PENDING_EVENT_FEEDBACK: value as 'DOT' | '',
			});
		},

		handleBorderColorBehaviorChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.target.value;
			form.setValue('tags', {
				...form.watch('tags'),
				TT_BORDER_COLOR_BEHAVIOR: value as 'EVENT_STATUS' | 'ATTENDEE_STATUS',
			});
		},

		handleStatusWhenDatesChangedChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.target.value;
			form.setValue('tags', {
				...form.watch('tags'),
				TT_STATUS_WHEN_DATES_CHANGED: value as 'SET_TENTATIVE' | 'KEEP',
			});
		},
	};
}
